import { byId, clear, el, setBusy, setHidden, setText } from "../dom";
import { channelLabel, formatCount, formatDistanceKm, formatEtaMinutes, formatRelative, isToday } from "../format";
import type { Household, NotificationEvent } from "../types";
import { emptyState, errorState, skeletonRows } from "./states";
import type { LoadStatus } from "./operation";

export interface AlertsState {
  status: LoadStatus;
  events: readonly NotificationEvent[];
  total: number;
  error: string | null;
}

export interface AlertsHandlers {
  onRetry(): void;
  onLoadMore(): void;
  onClearFilter(): void;
  onExport(): void;
}

export interface AlertsController {
  setState(state: AlertsState): void;
  setToday(count: number | null): void;
  setFilter(household: Household | null): void;
  setLoadingMore(busy: boolean): void;
  reset(): void;
}

const NEW_WINDOW_MS = 90_000;
const ARRIVED_WINDOW_MS = 1600;
const RELATIVE_TICK_MS = 15_000;

export function initAlerts(handlers: AlertsHandlers): AlertsController {
  const listElement = byId<HTMLUListElement>("alerts-list");
  const todayChip = byId<HTMLSpanElement>("alerts-today");
  const filterRow = byId<HTMLDivElement>("alerts-filter");
  const filterName = byId<HTMLSpanElement>("alerts-filter-name");
  const filterClear = byId<HTMLButtonElement>("alerts-filter-clear");
  const foot = byId<HTMLDivElement>("alerts-foot");
  const rangeLabel = byId<HTMLParagraphElement>("alerts-range");
  const moreButton = byId<HTMLButtonElement>("alerts-more");

  let events: readonly NotificationEvent[] = [];
  let status: LoadStatus = "idle";
  let error: string | null = null;
  let total = 0;
  let todayFromStats: number | null = null;
  const discoveredAt = new Map<string, number>();
  const arrivedAt = new Map<string, number>();
  let primed = false;

  function render(): void {
    const now = Date.now();
    const today = todayFromStats ?? events.filter((event) => isToday(event.created_at)).length;
    setText(todayChip, `Hoy: ${formatCount(today)}`);

    const scrollTop = listElement.scrollTop;
    clear(listElement);

    if (status === "idle" || status === "loading") {
      listElement.append(skeletonRows(3));
      setHidden(foot, true);
      return;
    }

    if (status === "error") {
      listElement.append(
        errorState({
          title: "No pudimos cargar las alertas",
          message: error ?? "Revisa la conexión e intenta de nuevo.",
          onRetry: handlers.onRetry,
        }),
      );
      setHidden(foot, true);
      return;
    }

    if (events.length === 0) {
      listElement.append(
        emptyState({
          iconName: "i-bell",
          title: "Sin alertas por ahora",
          message: "Cuando un camión se acerque a un hogar con la anticipación elegida, aparecerá aquí.",
        }),
      );
      setHidden(foot, true);
      return;
    }

    for (const event of events) {
      const arrived = arrivedAt.get(event.id);
      const isArrived = arrived !== undefined && now - arrived < ARRIVED_WINDOW_MS;
      if (arrived !== undefined && !isArrived) arrivedAt.delete(event.id);
      const seen = discoveredAt.get(event.id);
      const isNew = seen !== undefined && now - seen < NEW_WINDOW_MS;
      const item = el("li", {
        className: `alert${isNew ? " alert--new" : ""}${isArrived ? " alert--arrived" : ""}`,
      });

      const head = el("div", { className: "alert__head" });
      head.append(
        el("span", { className: "alert__title", text: event.household_name, title: event.household_name }),
        el("span", { className: "alert__time", text: formatRelative(event.created_at, now) }),
      );

      const figures = el("div", { className: "alert__figures" });
      figures.append(
        figure("ETA", formatEtaMinutes(event.eta_seconds)),
        figure("Distancia", formatDistanceKm(event.distance_m)),
      );

      const chips = el("div", { className: "alert__chips" });
      if (isNew) chips.append(el("span", { className: "chip chip--new", text: "Nueva" }));
      for (const channel of event.channels) {
        chips.append(el("span", { className: "chip", text: channelLabel(channel) }));
      }

      item.append(
        head,
        el("p", { className: "alert__route", text: `${event.vehicle_label} · ${event.route_name}` }),
        figures,
        chips,
      );
      listElement.append(item);
    }

    setHidden(foot, false);
    setText(
      rangeLabel,
      `Mostrando ${formatCount(events.length)} de ${formatCount(total)} ${
        total === 1 ? "alerta" : "alertas"
      }`,
    );
    setHidden(moreButton, events.length >= total);
    listElement.scrollTop = scrollTop;
  }

  function figure(label: string, value: string): HTMLElement {
    const wrap = el("span", { className: "figure" });
    wrap.append(
      el("span", { className: "figure__label", text: label }),
      el("span", { className: "figure__value", text: value }),
    );
    return wrap;
  }

  filterClear.addEventListener("click", handlers.onClearFilter);
  moreButton.addEventListener("click", handlers.onLoadMore);
  byId<HTMLButtonElement>("export-alerts").addEventListener("click", handlers.onExport);
  window.setInterval(render, RELATIVE_TICK_MS);
  render();

  return {
    setState(state) {
      if (state.status === "ready") {
        const now = Date.now();
        for (const event of state.events) {
          if (!discoveredAt.has(event.id)) {
            discoveredAt.set(event.id, primed ? now : 0);
            if (primed) arrivedAt.set(event.id, now);
          }
        }
        primed = true;
      }
      events = state.events;
      status = state.status;
      error = state.error;
      total = state.total;
      render();
    },

    setToday(count) {
      todayFromStats = count;
      render();
    },

    setFilter(household) {
      setHidden(filterRow, household === null);
      if (household !== null) setText(filterName, `Alertas de «${household.name}»`);
    },

    setLoadingMore(busy) {
      setBusy(moreButton, busy);
      if (busy) setText(moreButton, "Cargando…");
      else setText(moreButton, "Cargar más");
    },

    reset() {
      events = [];
      status = "loading";
      error = null;
      total = 0;
      discoveredAt.clear();
      arrivedAt.clear();
      primed = false;
      render();
    },
  };
}
